import { collection, getDocs, query, where } from 'firebase/firestore';

import { FSCollections } from '@src/constants/database';
import { db } from '@src/firebase/firebase';
import type {
  CancelAttendanceEventHttpResult,
  CancelAttendanceEventParams,
  CancelAttendanceEventResponseBody,
  CreateAttendanceEventParams,
  CreateAttendanceEventResponse,
  ListAttendanceEventsByRecordParams,
} from '@src/models/interfaces/attendanceEvent.interface';
import { getDocData } from '@src/packages/utils';
import { apiCallable } from '@src/util/cloud-functions/apiClient';
import { API_OPERATIONS } from '@src/util/cloud-functions/operations';
import { buildRestUrl } from '@src/util/cloud-functions/restEndpoint';
import safeExecute from '@src/util/safeExecute';

class AttendanceEventRepository {
  getAttendanceEventsPath(organizationId: string): string {
    return `${FSCollections.ORGANIZATIONS}/${organizationId}/${FSCollections.ATTENDANCE_EVENTS}`;
  }

  /**
   * Creates a new attendance event (marking) via the Cloud Function.
   * Repository layer: raw API call, no transformation, errors propagate to the Service.
   *
   * @throws Cloud Function errors as-is (e.g. functions/permission-denied).
   */
  async createAttendanceEvent(
    params: CreateAttendanceEventParams,
  ): Promise<CreateAttendanceEventResponse> {
    const newAttendanceEventFn = apiCallable<
      CreateAttendanceEventParams,
      CreateAttendanceEventResponse
    >(API_OPERATIONS.NEW_ATTENDANCE_EVENT);
    const response = await newAttendanceEventFn(params);
    return response.data;
  }

  /**
   * Lists raw Firestore attendance_events for a given attendance record.
   * Returns transport docs (id + fields); mapping happens in the service.
   */
  async listByAttendanceRecord({
    organizationId,
    attendanceRecordId,
  }: ListAttendanceEventsByRecordParams): Promise<unknown[]> {
    return safeExecute(async () => {
      if (!organizationId?.trim() || !attendanceRecordId?.trim()) {
        return [];
      }

      const eventsRef = collection(db, this.getAttendanceEventsPath(organizationId));
      const eventsQuery = query(
        eventsRef,
        where('attendanceRecordId', '==', attendanceRecordId),
      );
      const snapshot = await getDocs(eventsQuery);

      return snapshot.docs
        .map(docSnap => getDocData(docSnap))
        .filter((doc): doc is Record<string, unknown> => doc != null);
    });
  }

  async cancelAttendanceEvent(
    data: CancelAttendanceEventParams,
  ): Promise<CancelAttendanceEventResponseBody> {
    return safeExecute(async () => {
      const cancelAttendanceEventFn = apiCallable<
        CancelAttendanceEventParams,
        CancelAttendanceEventResponseBody
      >(API_OPERATIONS.CANCEL_ATTENDANCE_EVENT);
      const response = await cancelAttendanceEventFn(data);
      return response.data;
    });
  }

  async cancelAttendanceEventByToken(token: string): Promise<CancelAttendanceEventHttpResult> {
    return safeExecute(async () => {
      if (!token) {
        return {
          ok: false,
          status: 401,
          data: { error: 'Unauthenticated' },
        };
      }

      const endpoint = buildRestUrl('/attendance/cancelShiftChange');
      const endpointWithToken = buildRestUrl('/attendance/cancelShiftChange', { token });
      const response = await fetch(endpointWithToken, { method: 'POST' });
      const parsedResponse = await this.parseResponse(response);

      // Fallback path: some edge proxies drop query params.
      // Retry once with Bearer token if backend still replies Unauthenticated.
      if (parsedResponse.status === 401) {
        try {
          const fallbackResponse = await fetch(endpoint, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
          });
          return this.parseResponse(fallbackResponse);
        } catch (fallbackError) {
          return parsedResponse;
        }
      }

      return parsedResponse;
    });
  }

  async parseResponse(response: Response): Promise<CancelAttendanceEventHttpResult> {
    let body: CancelAttendanceEventResponseBody = {};
    try {
      body = await response.json();
    } catch (error) {
      body = {};
    }

    return {
      ok: response.ok,
      status: response.status,
      data: body,
    };
  }
}

export default AttendanceEventRepository;
